import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { SuperChartConfiguration_TimeBucket } from '../proto/generated/superchart-page'
import { TimeBucket_TimeSeries } from '../proto/generated/common'
import { LineType } from 'lightweight-charts'
import { DEFAULT_CUSTOM_CALL_BASE_COLOR, DEFAULT_CUSTOM_PUT_BASE_COLOR, DEFAULT_CUSTOM_CALL_HUE_SHIFT, DEFAULT_CUSTOM_PUT_HUE_SHIFT, DEFAULT_MULTI_STRIKE_COLOR_PALETTE_ID, MultiStrikeColorPaletteId } from '../components/multiStikeChart/multiStrikeColorPalettes'
import { ChartLineStyle } from '../components/colorSelector/lineStyle.types'
import { ScalesAndLinesSettings, StatusLineSettings, CanvasSettings } from '../components/chartV2/chart.typings'
import { defaultScalesAndLinesSettings } from '../util/scalesAndLinesDefaults'
import { defaultStatusLineSettings } from '../util/statusLineDefaults'
import { defaultCanvasSettings } from '../util/canvasSettingsDefaults'

export type MultiStrikeChartTypes = 'price' | 'iv' | 'oi' | 'oiChange' | 'cumOi'

export interface MultiStrikeChartStylesTypes {
    lineThickness: number
    lineType: LineType
    autoRefresh: boolean
    notifyInterval: number
    /** Last chosen Near ATM strike count, remembered independently of the active mode. */
    nearAtmCount: number
    colorPaletteId: MultiStrikeColorPaletteId
    customCallBaseColor: string
    customPutBaseColor: string
    customCallHueShift: number
    customPutHueShift: number
    /** Underlying (primary) series styling. Falls back to theme defaults when unset. */
    underlyingColor?: string
    underlyingThickness?: number
    underlyingLineStyle?: ChartLineStyle
    underlyingLineType?: LineType
    /** Strike (option) series line style. Thickness reuses lineThickness. */
    strikeLineStyle?: ChartLineStyle
}

export type MultiStrikeSecuritiesType = Record<string, CallPutType[]>

export type MultiStrikeSecuritiesClassType = Record<string, {
    callSecKey: string
    putSecKey: string
    callPut: CallPutType[]
}>

export interface MultiStrikeChartConfig {
    key? : string
    expiry?: string
    securities?: MultiStrikeSecuritiesType
    chartType?: MultiStrikeChartTypes
    styles?: MultiStrikeChartStylesTypes
    callPut?: CallPutType[]
    cumOi?: boolean,
    nearAtm?: number | undefined
    atm?: number | undefined,
    showChange?: boolean
}

export type CallPutType = 'CE' | 'PE'

export interface OIAnalysisSettings {
    showOI: boolean
    replayKey: number
    strikeCount: string
    selectedExpiries: string[] // Array of expiry dates
    securityKey?: string // Track which security these expiries belong to
    /** Expiry ids (epoch ms strings) for the expiries menu, from oichange API responses */
    expiriesMenuFromApi: string[]
    expiriesMenuLoading: boolean
    // For OI Change (intraday vs custom range)
    rangeType?: 'intraday' | 'customRange'
    customFromDate?: number // timestamp
    customToDate?: number // timestamp
    // Single-day analysis date (OI Change intraday + Open Interest)
    selectedDate?: number // timestamp
}

export interface OpenInterestInitialStateType {
    timeBucket: SuperChartConfiguration_TimeBucket
    securities: MultiStrikeSecuritiesType
    chartType: MultiStrikeChartTypes
    styles: MultiStrikeChartStylesTypes
    callPut: CallPutType[]
    cumOi: boolean,
    nearAtm: number | undefined
    atm: number | undefined
    loading: boolean,
    showChange: boolean,
    key : string
    scalesAndLinesSettings: ScalesAndLinesSettings
    statusLineSettings: StatusLineSettings
    canvasSettings: CanvasSettings
    oiChangeSettings: OIAnalysisSettings
    openInterestSettings: OIAnalysisSettings
}

export const homeInitialState: OpenInterestInitialStateType = {
    timeBucket: {
        code: 'Minute',
        name: '1 minute',
        timeSeries: TimeBucket_TimeSeries.MINUTE,
        timeValue: 1
    },
    key : '',
    securities: {},
    chartType: 'iv',
    styles: {
        lineThickness: 1,
        lineType: LineType.Simple,
        autoRefresh: true,
        notifyInterval: 30,
        nearAtmCount: 3,
        colorPaletteId: DEFAULT_MULTI_STRIKE_COLOR_PALETTE_ID,
        customCallBaseColor: DEFAULT_CUSTOM_CALL_BASE_COLOR,
        customPutBaseColor: DEFAULT_CUSTOM_PUT_BASE_COLOR,
        customCallHueShift: DEFAULT_CUSTOM_CALL_HUE_SHIFT,
        customPutHueShift: DEFAULT_CUSTOM_PUT_HUE_SHIFT,
        underlyingThickness: 1,
        underlyingLineStyle: 'large_dashed',
        underlyingLineType: LineType.Simple,
        strikeLineStyle: 'solid',
    },
    callPut: ['CE', 'PE'],
    nearAtm: undefined,
    atm: undefined,
    loading: false,
    cumOi: false,
    showChange: false,
    scalesAndLinesSettings: { ...defaultScalesAndLinesSettings },
    statusLineSettings: { ...defaultStatusLineSettings },
    canvasSettings: { ...defaultCanvasSettings },
    // OI Analysis screen settings
    oiChangeSettings: {
        showOI: false,
        replayKey: 0,
        strikeCount: '25',
        selectedExpiries: [],
        securityKey: undefined,
        expiriesMenuFromApi: [],
        expiriesMenuLoading: false,
        rangeType: 'intraday',
        customFromDate: undefined,
        customToDate: undefined,
        selectedDate: undefined
    },
    openInterestSettings: {
        showOI: false,
        replayKey: 0,
        strikeCount: '25',
        selectedExpiries: [],
        securityKey: undefined,
        expiriesMenuFromApi: [],
        expiriesMenuLoading: false,
        selectedDate: undefined
    }
}

const multiStrikeChartSlice = createSlice({
    name: 'home',
    initialState: homeInitialState,
    reducers: {
        setMultiStrikeChartTimeBucket(state: OpenInterestInitialStateType, action: PayloadAction<SuperChartConfiguration_TimeBucket>) {
            return {
                ...state,
                timeBucket: {
                    ...action.payload
                }
            }
        },
        setMultiStrikeSecurities(state: OpenInterestInitialStateType, action: PayloadAction<MultiStrikeSecuritiesType>) {
            return {
                ...state,
                securities: action.payload
            }
        },
        setMultiStrikeChartKey(state: OpenInterestInitialStateType, action: PayloadAction<string>) {
            return {
                ...state,
                key: action.payload
            }
        },
        setMultiStrikeChartType(state: OpenInterestInitialStateType, action: PayloadAction<MultiStrikeChartTypes>) {
            const nextType = action.payload
            return {
                ...state,
                chartType: nextType,
                showChange: false,
                cumOi: nextType === 'cumOi' ? state.cumOi : false,
            }
        },
        setMultiStrikeCumOi(state: OpenInterestInitialStateType, action: PayloadAction<boolean>) {
            return {
                ...state,
                cumOi: action.payload
            }
        },
        setMultiStrikeShowChange(state: OpenInterestInitialStateType, action: PayloadAction<boolean>) {
            return {
                ...state,
                showChange: action.payload
            }
        },
        setMultiStrikeCumOiToggle(
            state: OpenInterestInitialStateType,
            action: PayloadAction<{ chartType: MultiStrikeChartTypes; cumOi: boolean; showChange: boolean }>
        ) {
            return {
                ...state,
                chartType: action.payload.chartType,
                cumOi: action.payload.cumOi,
                showChange: action.payload.showChange,
            }
        },
        setMultiStrikeChartStyles(state: OpenInterestInitialStateType, action: PayloadAction<MultiStrikeChartStylesTypes>) {
            return {
                ...state,
                styles: action.payload
            }
        },
        setMultiStrikeScalesAndLinesSettings(state: OpenInterestInitialStateType, action: PayloadAction<ScalesAndLinesSettings>) {
            return {
                ...state,
                scalesAndLinesSettings: action.payload
            }
        },
        setMultiStrikeStatusLineSettings(state: OpenInterestInitialStateType, action: PayloadAction<StatusLineSettings>) {
            return {
                ...state,
                statusLineSettings: action.payload
            }
        },
        setMultiStrikeCanvasSettings(state: OpenInterestInitialStateType, action: PayloadAction<CanvasSettings>) {
            return {
                ...state,
                canvasSettings: action.payload
            }
        },
        setMultiStrikeShowCallPut(state: OpenInterestInitialStateType, action: PayloadAction<CallPutType[]>) {
            if (JSON.stringify(state.callPut) === JSON.stringify(action.payload)) {
                return state
            }
            return {
                ...state,
                callPut: action.payload
            }
        },
        setMultiStrikeNearAtm(state: OpenInterestInitialStateType, action: PayloadAction<{
            nearAtm: number | undefined
            atm: number | undefined
        }>) {
            return {
                ...state,
                nearAtm: action.payload.nearAtm,
                atm: action.payload.atm
            }
        },
        setMultiStrikeChartLoading(state: OpenInterestInitialStateType, action: PayloadAction<boolean>) {
            return {
                ...state,
                loading: action.payload
            }
        },
        // OI Change settings
        setOIChangeSettings(state: OpenInterestInitialStateType, action: PayloadAction<Partial<OIAnalysisSettings>>) {
            return {
                ...state,
                oiChangeSettings: {
                    ...state.oiChangeSettings,
                    ...action.payload
                }
            }
        },
        // Open Interest settings
        setOpenInterestSettings(state: OpenInterestInitialStateType, action: PayloadAction<Partial<OIAnalysisSettings>>) {
            return {
                ...state,
                openInterestSettings: {
                    ...state.openInterestSettings,
                    ...action.payload
                }
            }
        }
    }
})

const { actions, reducer } = multiStrikeChartSlice

export const {
    setMultiStrikeChartTimeBucket,
    setMultiStrikeSecurities,
    setMultiStrikeChartType,
    setMultiStrikeChartStyles,
    setMultiStrikeScalesAndLinesSettings,
    setMultiStrikeStatusLineSettings,
    setMultiStrikeCanvasSettings,
    setMultiStrikeShowCallPut,
    setMultiStrikeNearAtm,
    setMultiStrikeChartLoading,
    setMultiStrikeCumOi,
    setMultiStrikeShowChange,
    setMultiStrikeCumOiToggle,
    setMultiStrikeChartKey,
    setOIChangeSettings,
    setOpenInterestSettings
} = actions


export default reducer
